import { useEffect, useRef, useState } from "preact/hooks";
import { GuestNav } from "../chrome/guest-nav.js";
import { Icon } from "../chrome/icons.js";
import { Ring } from "../chrome/ring.js";
import { captureVideo, keepCamera, keptFacing, readFile, type Captured } from "../image.js";
import { routeHref } from "../routes.js";
import { PHOTO_QUOTA } from "../types.js";

export interface CameraScreenProps {
  readonly eventName: string;
  readonly accent: string;
  readonly left: number;
  readonly lastUrl: string | null;
  readonly tipSeen: boolean;
  readonly media: MediaDevices | undefined;
  readonly samples: readonly string[];
  onTipSeen(): void;
  onSend(captured: Captured): void;
}

type Phase =
  | { readonly kind: "starting" }
  | { readonly kind: "live" }
  | { readonly kind: "denied" }
  | { readonly kind: "preview"; readonly captured: Captured; readonly url: string };

export function CameraScreen({
  eventName,
  accent,
  left,
  lastUrl,
  tipSeen,
  media,
  samples,
  onTipSeen,
  onSend,
}: CameraScreenProps) {
  const [facing, setFacing] = useState(keptFacing);
  const [phase, setPhase] = useState<Phase>({ kind: "starting" });
  const [toast, setToast] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const file = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    void keepCamera(facing, media).then((opened) => {
      if (!active) {
        return;
      }
      const element = video.current;
      if (!opened || !element) {
        setPhase({ kind: "denied" });
        return;
      }
      stream.current = opened;
      element.onloadedmetadata = () =>
        setPhase((current) => (current.kind === "preview" ? current : { kind: "live" }));
      element.srcObject = opened;
      void element.play()?.catch(() => undefined);
    });
    return () => {
      active = false;
      stream.current = null;
    };
  }, [facing, media]);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const preview = (captured: Captured) =>
    setPhase({
      kind: "preview",
      captured,
      url: URL.createObjectURL(new Blob([captured.image.bytes], { type: captured.image.type })),
    });

  const leavePreview = () => {
    if (phase.kind === "preview") {
      URL.revokeObjectURL(phase.url);
    }
    setPhase({ kind: stream.current ? "live" : "denied" });
  };

  const pickSample = async (src: string) => {
    if (left <= 0) {
      return;
    }
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      preview(await readFile(new File([blob], "casamento.webp", { type: blob.type })));
    } catch {
      setToast("Não deu para abrir essa foto.");
    }
  };

  const sampleRow = (
    <div class="d-samples">
      <span>Ou use uma foto do casamento</span>
      <div class="d-samples-list">
        {samples.map((src, index) => (
          <button
            key={src}
            type="button"
            class="d-sample"
            aria-label={`Usar a foto do casamento ${index + 1}`}
            disabled={left <= 0}
            onClick={() => void pickSample(src)}
          >
            <img src={src} alt="" />
          </button>
        ))}
      </div>
    </div>
  );

  const shoot = async () => {
    const element = video.current;
    if (!element || element.videoWidth === 0 || left <= 0) {
      return;
    }
    preview(await captureVideo(element, facing === "user"));
  };

  const send = () => {
    if (phase.kind !== "preview") {
      return;
    }
    onSend(phase.captured);
    leavePreview();
    setToast("Foto enviada.");
  };

  const previewing = phase.kind === "preview";
  const denied = phase.kind === "denied";
  return (
    <div
      class={denied ? "d-screen d-camera is-denied" : "d-screen d-camera"}
      data-theme="dark"
      data-testid="screen-capture"
    >
      <div class="d-camera-media">
        <video
          ref={video}
          class={facing === "user" ? "d-viewfinder is-mirrored" : "d-viewfinder"}
          data-testid="viewfinder"
          muted
          playsInline
          autoplay
          hidden={phase.kind !== "live"}
        />
        {phase.kind === "preview" ? (
          <img
            class="d-capture-preview"
            data-testid="capture-preview"
            src={phase.url}
            alt="Prévia da foto"
          />
        ) : null}
      </div>
      <div class="d-camera-top">
        <a class="d-icon-button" href={routeHref("album")} aria-label="Fechar">
          <Icon name="x" />
        </a>
        <h1 class="d-camera-title">{eventName}</h1>
        <button type="button" class="d-icon-button" aria-label="Flash" disabled>
          <Icon name="zap" />
        </button>
      </div>
      {previewing ? null : (
        <div class="d-camera-row">
          <span class="d-ring-chip" data-testid="ring-chip">
            <Ring size={34} stroke={3} fraction={left / PHOTO_QUOTA} color={accent}>
              <span data-testid="ring-count">{left}</span>
            </Ring>
            <span>{left === 1 ? "foto restante" : "fotos restantes"}</span>
          </span>
        </div>
      )}
      {!tipSeen && !previewing && !denied ? (
        <div class="d-tip" role="status">
          <p>Suas fotos vão para o álbum do evento e aparecem no telão.</p>
          <button type="button" class="d-link" onClick={onTipSeen}>
            Entendi
          </button>
        </div>
      ) : null}
      {left <= 0 && !previewing ? (
        <p class="d-notice" data-testid="capture-blocked">
          Suas fotos acabaram
        </p>
      ) : null}
      {phase.kind === "denied" ? (
        <div class="d-empty" data-testid="camera-denied">
          <h2>Sem acesso à câmera</h2>
          <p>
            O navegador não permitiu usar a câmera. Você pode liberar nas configurações ou enviar
            fotos da galeria.
          </p>
          <button
            type="button"
            class="d-button d-primary"
            disabled={left <= 0}
            onClick={() => file.current?.click()}
          >
            Usar a galeria
          </button>
          {sampleRow}
        </div>
      ) : null}
      <input
        ref={file}
        type="file"
        accept="image/*"
        class="d-hidden-input"
        data-testid="camera-input"
        tabIndex={-1}
        onChange={(event) => {
          const input = event.currentTarget as HTMLInputElement;
          const chosen = input.files?.[0];
          input.value = "";
          if (chosen && left > 0) {
            void readFile(chosen).then(preview, () => setToast("Não deu para abrir essa foto."));
          }
        }}
      />
      {previewing ? (
        <div class="d-guest-bar">
          <button
            type="button"
            class="d-button d-secondary d-large"
            data-testid="capture-discard"
            onClick={leavePreview}
          >
            Descartar
          </button>
          <button
            type="button"
            class="d-button d-primary d-large"
            data-testid="capture-send"
            onClick={send}
          >
            Enviar
          </button>
        </div>
      ) : (
        <div class="d-camera-bottom">
          {toast ? (
            <p class="d-toast" data-testid="toast" role="status">
              {toast}
            </p>
          ) : null}
          {denied ? null : sampleRow}
          {denied ? null : (
            <div class="d-camera-controls">
              {lastUrl ? (
                <img class="d-last" src={lastUrl} alt="Última foto" />
              ) : (
                <span class="d-last" />
              )}
              <span class="d-shutter">
                <Ring size={84} stroke={4} fraction={left / PHOTO_QUOTA} color={accent} />
                <button
                  type="button"
                  class="d-shutter-button"
                  data-testid="camera-button"
                  data-guide-target="camera"
                  aria-label={`Tirar foto · ${left} restantes`}
                  disabled={phase.kind !== "live" || left <= 0}
                  onClick={() => void shoot()}
                />
              </span>
              <button
                type="button"
                class="d-icon-button"
                aria-label="Trocar câmera"
                disabled={phase.kind !== "live"}
                onClick={() => setFacing((side) => (side === "user" ? "environment" : "user"))}
              >
                <Icon name="switch" />
              </button>
            </div>
          )}
          <GuestNav active="camera" onPhoto />
        </div>
      )}
    </div>
  );
}
